import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { ScrollArea } from '../../ui/scroll-area';
import { MessageSquare, Send, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const GovernanceFeedbackSection = ({ pillar, isAdmin, isLeadership, currentUser, onSave }) => {
  const [newSuggestion, setNewSuggestion] = useState('');
  const [suggestions, setSuggestions] = useState(pillar?.suggestions || []);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const scrollRef = useRef(null);
  const hasMarkedSeen = useRef(false);

  useEffect(() => {
    setSuggestions(pillar?.suggestions || []);
    hasMarkedSeen.current = false;
  }, [pillar]);

  useEffect(() => {
    if (scrollRef.current) {
      const scrollElement = scrollRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollElement) {
        scrollElement.scrollTop = scrollElement.scrollHeight;
      }
    }
  }, [suggestions]);

  const formatContent = (content) => {
    return content?.replace(/@(\w+\s?\w*)/g, '<span class="text-blue-400 font-medium">@$1</span>') || '';
  };

  useEffect(() => {
    if (isLeadership && !isAdmin && !hasMarkedSeen.current && suggestions.length > 0) {
      const userId = currentUser?.id;
      const needsUpdate = suggestions.some(s => {
        const lastReply = s.replies?.[s.replies.length - 1];
        const hasAdminReply = lastReply && (lastReply.is_admin || lastReply.user_role === 'super_admin' || lastReply.user_role === 'admin');
        return hasAdminReply && (!s.seen_by || !s.seen_by.includes(userId));
      });
      
      if (needsUpdate) {
        const updatedSuggestions = suggestions.map(s => {
          const lastReply = s.replies?.[s.replies.length - 1];
          const hasAdminReply = lastReply && (lastReply.is_admin || lastReply.user_role === 'super_admin' || lastReply.user_role === 'admin');
          if (hasAdminReply) {
            const seenBy = s.seen_by || [];
            if (!seenBy.includes(userId)) {
              return { ...s, seen_by: [...seenBy, userId] };
            }
          }
          return s;
        });
        setSuggestions(updatedSuggestions);
        hasMarkedSeen.current = true;
        onSave(pillar.id, { suggestions: updatedSuggestions }).catch(() => {});
      }
    }
  }, [isLeadership, isAdmin, suggestions, currentUser, onSave, pillar?.id]);

  const handleAddSuggestion = async () => {
    if (!newSuggestion.trim()) return;
    const suggestion = {
      id: `sug_${Date.now()}`,
      content: newSuggestion.trim(),
      user_id: currentUser?.id,
      user_name: `${currentUser?.first_name || ''} ${currentUser?.last_name || ''}`.trim() || 'Leadership',
      user_role: currentUser?.role,
      created_at: new Date().toISOString(),
      resolved: false,
      replies: []
    };
    const updatedSuggestions = [...suggestions, suggestion];
    setSuggestions(updatedSuggestions);
    setNewSuggestion('');
    try {
      await onSave(pillar.id, { suggestions: updatedSuggestions });
      toast.success('Suggestion sent to dev team');
    } catch (e) {
      toast.error('Failed to send suggestion');
    }
  };

  const handleResolveSuggestion = async (suggestionId) => {
    const adminName = `${currentUser?.first_name || ''} ${currentUser?.last_name || ''}`.trim() || 'Admin';
    const updatedSuggestions = suggestions.map(s => 
      s.id === suggestionId ? { 
        ...s, 
        resolved: true, 
        resolved_at: new Date().toISOString(),
        resolved_by: adminName
      } : s
    );
    setSuggestions(updatedSuggestions);
    setReplyingTo(null);
    setReplyText('');
    try {
      await onSave(pillar.id, { suggestions: updatedSuggestions });
      toast.success('Suggestion resolved');
    } catch (e) {
      toast.error('Failed to update');
    }
  };

  const handleAddReply = async (suggestionId) => {
    if (!replyText.trim()) return;
    const userName = `${currentUser?.first_name || ''} ${currentUser?.last_name || ''}`.trim() || 'User';
    const reply = {
      id: `reply_${Date.now()}`,
      content: replyText.trim(),
      user_name: userName,
      user_role: currentUser?.role,
      is_admin: isAdmin,
      created_at: new Date().toISOString()
    };
    const updatedSuggestions = suggestions.map(s => 
      s.id === suggestionId ? { 
        ...s, 
        replies: [...(s.replies || []), reply],
        ...(isAdmin ? { 
          resolved: true, 
          resolved_at: new Date().toISOString(),
          resolved_by: userName 
        } : {})
      } : s
    );
    setSuggestions(updatedSuggestions);
    setReplyingTo(null);
    setReplyText('');
    try {
      await onSave(pillar.id, { suggestions: updatedSuggestions });
      toast.success(isAdmin ? 'Reply sent & resolved' : 'Reply sent');
    } catch (e) {
      toast.error('Failed to send reply');
    }
  };

  if (!isLeadership && !isAdmin) return null;

  const unresolvedCount = suggestions.filter(s => !s.resolved).length;

  const getReplyStyle = (reply) => {
    if (reply.is_admin || reply.user_role === 'super_admin' || reply.user_role === 'admin') {
      return { bg: 'bg-blue-500/10', labelColor: 'text-blue-700', label: 'Dev Team' };
    }
    return { bg: 'bg-amber-500/10', labelColor: 'text-amber-700', label: 'Leadership' };
  };

  return (
    <div className="border-t pt-4 mt-4">
      <div className="flex items-center justify-between mb-3">
        <Label className="text-sm font-medium flex items-center gap-2">
          <MessageSquare className="h-4 w-4" />
          Feedback & Suggestions
          {isAdmin && unresolvedCount > 0 && (
            <Badge variant="destructive" className="text-[10px]">{unresolvedCount} new</Badge>
          )}
        </Label>
        {suggestions.length > 2 && (
          <span className="text-[10px] text-muted-foreground">↕ Scroll for more</span>
        )}
      </div>
      
      <ScrollArea className="h-[300px] border rounded-lg p-2 bg-background/50" ref={scrollRef}>
        <div className="space-y-3">
          {suggestions.length > 0 ? suggestions.map((sug) => {
            const lastReply = sug.replies?.[sug.replies.length - 1];
            const hasAdminReply = lastReply && (lastReply.is_admin || lastReply.user_role === 'super_admin' || lastReply.user_role === 'admin');
            const leadershipCanReply = isLeadership && !isAdmin && hasAdminReply;
            const isUnseen = isLeadership && !isAdmin && hasAdminReply && (!sug.seen_by || !sug.seen_by.includes(currentUser?.id));
            
            let cardStyle = sug.resolved ? 'bg-muted/30 border border-muted' : 'bg-amber-500/10 border border-amber-500/30';
            if (isUnseen) {
              cardStyle = 'bg-blue-500/10 border-2 border-blue-500 ring-2 ring-blue-500/20';
            }
            
            return (
            <div key={sug.id} className={`p-3 rounded-lg text-sm ${cardStyle} relative`}>
              {isUnseen && (
                <div className="absolute -top-2 -right-2">
                  <Badge className="bg-blue-500 text-white text-[9px] px-1.5 animate-pulse">NEW</Badge>
                </div>
              )}
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-xs">{sug.user_name} <span className="text-muted-foreground">({sug.user_role?.replace('_', ' ')})</span></span>
                <div className="flex gap-1 items-center">
                  {sug.resolved ? (
                    <>
                      <Badge variant="secondary" className="text-[9px] bg-green-500/20 text-green-700">✓ Resolved</Badge>
                      {leadershipCanReply && (
                        <Button variant="ghost" size="sm" className="h-5 text-[10px] px-2 text-blue-600" onClick={() => setReplyingTo(replyingTo === sug.id ? null : sug.id)}>
                          <MessageSquare className="h-3 w-3 mr-1" /> Reply
                        </Button>
                      )}
                    </>
                  ) : (
                    <>
                      <Button variant="ghost" size="sm" className="h-5 text-[10px] px-2" onClick={() => setReplyingTo(replyingTo === sug.id ? null : sug.id)}>
                        <MessageSquare className="h-3 w-3 mr-1" /> Reply
                      </Button>
                      {isAdmin && (
                        <Button variant="ghost" size="sm" className="h-5 text-[10px] px-2 text-green-600" onClick={() => handleResolveSuggestion(sug.id)}>
                          <CheckCircle2 className="h-3 w-3 mr-1" /> Resolve
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </div>
              
              <p className="text-xs whitespace-pre-wrap mb-2">{sug.content}</p>
              <p className="text-[10px] text-muted-foreground">Sent: {new Date(sug.created_at).toLocaleString()}</p>
              
              {sug.resolved && (
                <div className="mt-2 pt-2 border-t border-muted">
                  <p className="text-[10px] text-green-600">
                    ✓ Resolved by {sug.resolved_by} on {new Date(sug.resolved_at).toLocaleString()}
                  </p>
                  {sug.resolve_note && (
                    <p className="text-xs mt-1 italic text-muted-foreground">"{sug.resolve_note}"</p>
                  )}
                </div>
              )}
              
              {sug.replies?.length > 0 && (
                <div className="mt-2 pt-2 border-t border-muted space-y-2">
                  {sug.replies.map(reply => {
                    const style = getReplyStyle(reply);
                    return (
                      <div key={reply.id} className={`${style.bg} p-2 rounded text-xs`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className={`font-medium ${style.labelColor}`}>
                            {reply.user_name} <span className="opacity-70">({style.label})</span>
                          </span>
                          <span className="text-[9px] text-muted-foreground">{new Date(reply.created_at).toLocaleString()}</span>
                        </div>
                        <p className="whitespace-pre-wrap">{reply.content}</p>
                      </div>
                    );
                  })}
                </div>
              )}
              
              {replyingTo === sug.id && (!sug.resolved || leadershipCanReply) && (
                <div className="mt-2 pt-2 border-t border-muted space-y-2">
                  <Textarea 
                    placeholder={isAdmin ? "Reply to this suggestion..." : "Reply to the dev team..."} 
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    rows={2}
                    className="text-xs"
                    autoFocus
                  />
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={() => { setReplyingTo(null); setReplyText(''); }}>
                      Cancel
                    </Button>
                    <Button size="sm" className="h-6 text-[10px]" onClick={() => handleAddReply(sug.id)} disabled={!replyText.trim()}>
                      <Send className="h-3 w-3 mr-1" /> Send Reply
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}) : (
            <p className="text-xs text-muted-foreground text-center py-4">
              {isAdmin ? 'No suggestions from leadership yet' : 'Leave a suggestion for the dev team'}
            </p>
          )}
        </div>
      </ScrollArea>
      
      {isLeadership && !isAdmin && (
        <div className="space-y-2 mt-4 pt-4 border-t border-muted">
          <Textarea 
            placeholder="Leave a note or suggestion for the dev team..." 
            value={newSuggestion} 
            onChange={(e) => setNewSuggestion(e.target.value)} 
            rows={2}
            className="text-sm"
          />
          <Button size="sm" onClick={handleAddSuggestion} disabled={!newSuggestion.trim()} className="w-full">
            <Send className="h-3 w-3 mr-1" /> Send to Dev Team
          </Button>
        </div>
      )}
    </div>
  );
};

export default GovernanceFeedbackSection;
